import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  Course,
  Cohort,
  Enrollment,
  Tenant,
  PlatformStats
} from '../../services/admin.service';

interface AdminState {
  // Courses
  courses: Course[];
  selectedCourse: Course | null;
  coursesLoading: boolean;
  coursesError: string | null;

  // Cohorts
  cohorts: Cohort[];
  selectedCohort: Cohort | null;
  cohortsLoading: boolean;
  cohortsError: string | null;

  // Enrollments
  enrollments: Enrollment[];
  enrollmentsLoading: boolean;
  enrollmentsError: string | null;

  // Tenants (super admin)
  tenants: Tenant[];
  selectedTenant: Tenant | null;
  tenantsLoading: boolean;
  tenantsError: string | null;

  // Platform stats (super admin)
  platformStats: PlatformStats | null;
  statsLoading: boolean;

  // UI state
  activeTab: 'courses' | 'cohorts' | 'enrollments' | 'tenants' | 'stats';
}

const initialState: AdminState = {
  courses: [],
  selectedCourse: null,
  coursesLoading: false,
  coursesError: null,

  cohorts: [],
  selectedCohort: null,
  cohortsLoading: false,
  cohortsError: null,

  enrollments: [],
  enrollmentsLoading: false,
  enrollmentsError: null,

  tenants: [],
  selectedTenant: null,
  tenantsLoading: false,
  tenantsError: null,

  platformStats: null,
  statsLoading: false,

  activeTab: 'courses',
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Tab navigation
    setActiveTab: (state, action: PayloadAction<AdminState['activeTab']>) => {
      state.activeTab = action.payload;
    },

    // Courses
    setCoursesLoading: (state, action: PayloadAction<boolean>) => {
      state.coursesLoading = action.payload;
    },
    setCourses: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload;
      state.coursesLoading = false;
      state.coursesError = null;
    },
    setCoursesError: (state, action: PayloadAction<string>) => {
      state.coursesError = action.payload;
      state.coursesLoading = false;
    },
    setSelectedCourse: (state, action: PayloadAction<Course | null>) => {
      state.selectedCourse = action.payload;
    },
    addCourse: (state, action: PayloadAction<Course>) => {
      state.courses.unshift(action.payload);
    },
    updateCourse: (state, action: PayloadAction<Course>) => {
      const index = state.courses.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.courses[index] = action.payload;
      }
      if (state.selectedCourse?.id === action.payload.id) {
        state.selectedCourse = action.payload;
      }
    },
    removeCourse: (state, action: PayloadAction<string>) => {
      state.courses = state.courses.filter(c => c.id !== action.payload);
      if (state.selectedCourse?.id === action.payload) {
        state.selectedCourse = null;
      }
    },

    // Cohorts
    setCohortsLoading: (state, action: PayloadAction<boolean>) => {
      state.cohortsLoading = action.payload;
    },
    setCohorts: (state, action: PayloadAction<Cohort[]>) => {
      state.cohorts = action.payload;
      state.cohortsLoading = false;
      state.cohortsError = null;
    },
    setCohortsError: (state, action: PayloadAction<string>) => {
      state.cohortsError = action.payload;
      state.cohortsLoading = false;
    },
    setSelectedCohort: (state, action: PayloadAction<Cohort | null>) => {
      state.selectedCohort = action.payload;
    },
    addCohort: (state, action: PayloadAction<Cohort>) => {
      state.cohorts.unshift(action.payload);
    },
    updateCohort: (state, action: PayloadAction<Cohort>) => {
      const index = state.cohorts.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.cohorts[index] = action.payload;
      }
      if (state.selectedCohort?.id === action.payload.id) {
        state.selectedCohort = action.payload;
      }
    },
    removeCohort: (state, action: PayloadAction<string>) => {
      state.cohorts = state.cohorts.filter(c => c.id !== action.payload);
      if (state.selectedCohort?.id === action.payload) {
        state.selectedCohort = null;
      }
    },

    // Enrollments
    setEnrollmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.enrollmentsLoading = action.payload;
    },
    setEnrollments: (state, action: PayloadAction<Enrollment[]>) => {
      state.enrollments = action.payload;
      state.enrollmentsLoading = false;
      state.enrollmentsError = null;
    },
    setEnrollmentsError: (state, action: PayloadAction<string>) => {
      state.enrollmentsError = action.payload;
      state.enrollmentsLoading = false;
    },
    addEnrollment: (state, action: PayloadAction<Enrollment>) => {
      state.enrollments.push(action.payload);
    },
    updateEnrollment: (state, action: PayloadAction<Enrollment>) => {
      const index = state.enrollments.findIndex(e => e.id === action.payload.id);
      if (index !== -1) {
        state.enrollments[index] = action.payload;
      }
    },
    removeEnrollment: (state, action: PayloadAction<string>) => {
      state.enrollments = state.enrollments.filter(e => e.id !== action.payload);
    },

    // Tenants (super admin)
    setTenantsLoading: (state, action: PayloadAction<boolean>) => {
      state.tenantsLoading = action.payload;
    },
    setTenants: (state, action: PayloadAction<Tenant[]>) => {
      state.tenants = action.payload;
      state.tenantsLoading = false;
      state.tenantsError = null;
    },
    setTenantsError: (state, action: PayloadAction<string>) => {
      state.tenantsError = action.payload;
      state.tenantsLoading = false;
    },
    setSelectedTenant: (state, action: PayloadAction<Tenant | null>) => {
      state.selectedTenant = action.payload;
    },
    addTenant: (state, action: PayloadAction<Tenant>) => {
      state.tenants.unshift(action.payload);
    },
    updateTenant: (state, action: PayloadAction<Tenant>) => {
      const index = state.tenants.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.tenants[index] = action.payload;
      }
      if (state.selectedTenant?.id === action.payload.id) {
        state.selectedTenant = action.payload as Tenant;
      }
    },

    // Platform stats
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },
    setPlatformStats: (state, action: PayloadAction<PlatformStats>) => {
      state.platformStats = action.payload;
      state.statsLoading = false;
    },

    // Reset state
    resetAdminState: () => initialState,
  },
});

export const {
  setActiveTab,
  setCoursesLoading,
  setCourses,
  setCoursesError,
  setSelectedCourse,
  addCourse,
  updateCourse,
  removeCourse,
  setCohortsLoading,
  setCohorts,
  setCohortsError,
  setSelectedCohort,
  addCohort,
  updateCohort,
  removeCohort,
  setEnrollmentsLoading,
  setEnrollments,
  setEnrollmentsError,
  addEnrollment,
  updateEnrollment,
  removeEnrollment,
  setTenantsLoading,
  setTenants,
  setTenantsError,
  setSelectedTenant,
  addTenant,
  updateTenant,
  setStatsLoading,
  setPlatformStats,
  resetAdminState,
} = adminSlice.actions;

export default adminSlice.reducer;
